/**
 * Recursive function.
 * Performs a fetch request with exponential backoff retry logic.
 *
 * @param {string} url The absolute URL to fetch (get).
 * @param {(data: any) => void} onSuccess Callback executed when a successful response is received.
 * @param {Object} [options]
 * @param {number} [options.retries=5] Maximum number of retry attempts.
 * @param {number} [options.delay=500] Initial delay in milliseconds before retrying (doubles each attempt).
 * @param {AbortSignal} [options.signal] Optional abort signal to cancel the request.
 * @param {RequestInit} [options.fetchOptions] Optional fetch configuration (headers, method, etc.).
 * @returns {Promise<void>}
 */
export async function fetchWithRetry(
    url,
    onSuccess,
    { retries = 5, delay = 500, signal, fetchOptions = {} } = {}
) {
    try {
        const res = await fetch(url, { ...fetchOptions, signal });
        const data = await res.json();

        if (res.ok) {
            onSuccess(data);
            return;
        }

        throw new Error('Request failed');
    } catch {
        if (retries > 0 && !(signal && signal.aborted)) {
            await new Promise((resolve) => setTimeout(resolve, delay));
            return fetchWithRetry(url, onSuccess, {
                retries: retries - 1,
                delay: delay * 2,
                signal,
                fetchOptions
            });
        }
    }
}
